import React, { forwardRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  Button,
  CalendarIcon,
  Caption1,
  DisplayHours,
  EditableText,
  EstimatePlusIcon,
  formatHours,
  IconButton,
  Input,
  Tooltip,
} from "@activecollab/components";
import { useDragContext } from "@activecollab/yank-and-drop";
import classNames from "classnames";
import PropTypes from "prop-types";
import { ActiveMembersAssigneePicker } from "~/components/ActiveMembersAssigneePicker";
import SelectTimeEstimate from "~/components/component.select_time_estimate";
import { MultiAssigneesPicker } from "~/components/MultiAssigneesPicker/MultiAssigneesPicker";
import TaskFormSelectDate from "~/components/tasks/TaskFormSelectDate";
import { getIsMultipleAssigneesEnabled, getPersonalizedDateFormat } from "~/store/selectors/selector.configOptions";
import { getJobTypesWithIdsAndNames } from "~/store/selectors/selector.jobTypes";
import { formatDateRange, formatDateSmart } from "~/utils/date";
import getDueIndicator from "~/utils/getDueIndicator.jsx";
import { SUGGESTION } from "./suggestionUtils";
import { ProjectMembersAssigneePicker } from "../../ProjectMembersAssigneePicker";

const inputProps = {
  onClick: (e) => {
    e.preventDefault();
    e.stopPropagation();
  },
  "aria-label": "editName",
};

export const DateSelectTrigger = forwardRef(({ value, tooltipValue, onClick, open, isOverdue }, ref) => {
  const { t } = useTranslation();
  return (
    <div className="tw-flex tw-justify-end tw-items-center tw-flex-shrink-0">
      <Tooltip title={value ? tooltipValue : t("Add Date")}>
        <Button
          className={classNames("show-on-hover c-cooltip c-cooltip--top tw-overflow-visible", {
            open: open || value,
            "tw-w-fit tw-px-2": value,
          })}
          variant="text gray"
          active={open}
          ref={ref}
          onClick={onClick}
        >
          {value ? (
            <Caption1
              weight="bold"
              whitespace="nowrap"
              className={classNames({
                "tw-text-red-alert": isOverdue,
              })}
            >
              {value}
            </Caption1>
          ) : (
            <CalendarIcon />
          )}
        </Button>
      </Tooltip>
    </div>
  );
});

DateSelectTrigger.propTypes = {
  value: PropTypes.string,
  tooltipValue: PropTypes.string,
  onClick: PropTypes.func,
  open: PropTypes.bool,
  isOverdue: PropTypes.bool,
};

DateSelectTrigger.displayName = "DateSelectTrigger";

export const SuggestedItem = ({
  id,
  projectId,
  name,
  type,
  assigneeId,
  startOn,
  dueOn,
  onUpdate,
  autoFocus,
  selectionMode,
  disabled,
  isPreview,
  shouldShowEstimate,
  allowAllUsers,
  estimate,
  jobTypeId,
  invalid,
  inputName,
  assigneeIds,
}) => {
  const format = useSelector(getPersonalizedDateFormat);
  const { dragState } = useDragContext();

  const date = useMemo(() => {
    return {
      from: startOn,
      to: dueOn,
    };
  }, [dueOn, startOn]);

  const formattedDate = formatDateRange(startOn, dueOn ?? startOn, format);
  const fullStartDate = formatDateSmart(startOn, format, true);
  const fullDueDate = formatDateSmart(dueOn, format, true);
  const jobTypesMap = useSelector(getJobTypesWithIdsAndNames);
  const { t } = useTranslation();
  const multiAssigneesEnabled = useSelector(getIsMultipleAssigneesEnabled);

  const tooltipText = useMemo(
    () => (startOn === dueOn ? fullDueDate : `${fullStartDate} - ${fullDueDate}`),
    [dueOn, fullDueDate, fullStartDate, startOn]
  );

  const isOverdue = useMemo(() => {
    return getDueIndicator(dueOn) === "overdue";
  }, [dueOn]);

  const handleChangeAssignee = useCallback(
    (userId) => {
      onUpdate(id, "assignee_id", userId);
    },
    [id, onUpdate]
  );

  const handleMultiAssigneesChange = useCallback(
    (val) => {
      onUpdate(id, "assignee_ids", val);
    },
    [id, onUpdate]
  );

  const handleChangeDate = useCallback(
    (date) => {
      onUpdate(id, "start_on", date.startOn);
      onUpdate(id, "due_on", date.dueOn);
    },
    [id, onUpdate]
  );

  const handleRename = useCallback(
    (e) => {
      onUpdate(id, "name", e.target.value);
    },
    [id, onUpdate]
  );

  const handleEstimate = useCallback(
    ({ estimate, job_type_id }) => {
      onUpdate(id, "estimate", estimate);
      onUpdate(id, "job_type_id", job_type_id);
    },
    [id, onUpdate]
  );

  return (
    <div
      className={classNames(
        "tw-flex tw-justify-between tw-items-center tw-pr-1 tw-gap-3 tw-rounded-md tw-flex-1 tw-min-w-0 tw-truncate",
        {
          "task-list-item tw-px-1": type === SUGGESTION,
        },
        {
          "tw-pointer-events-none": disabled || dragState?.isDragging,
        }
      )}
    >
      {type === SUGGESTION ? (
        <EditableText
          className={classNames("tw-truncate", {
            "tw-pointer-events-none": disabled,
          })}
          value={name}
          variant="Body 2"
          weight="medium"
          onSave={handleRename}
          inputProps={inputProps}
          disabled={disabled}
        />
      ) : (
        <Input
          type="text"
          className={classNames("tw-truncate tw-flex-1", {
            "tw-pointer-events-none": disabled,
          })}
          value={name}
          onChange={handleRename}
          disabled={disabled || dragState?.isDragging}
          autoFocus={autoFocus && !disabled}
          invalid={invalid}
          name={inputName}
        />
      )}
      {!isPreview ? (
        <div className="tw-flex tw-gap-4">
          <TaskFormSelectDate
            userId={Number(assigneeId)}
            from={date.from}
            to={date.to}
            onChange={handleChangeDate}
            target={
              <DateSelectTrigger
                value={formattedDate}
                tooltipValue={tooltipText}
                isOverdue={isOverdue}
              />
            }
            changeMode="instant"
            selectionMode={selectionMode}
            position="bottom-end"
          />
          {allowAllUsers ? (
            <>
              {multiAssigneesEnabled ? (
                <MultiAssigneesPicker
                  hiddenFromClients
                  assigneeIds={assigneeIds}
                  onChange={handleMultiAssigneesChange}
                  canEdit={true}
                  className={classNames({
                    "show-on-hover": !assigneeIds || assigneeIds?.length === 0,
                  })}
                />
              ) : (
                <ActiveMembersAssigneePicker
                  assigneeId={assigneeId}
                  onChange={handleChangeAssignee}
                  className={classNames({
                    "show-on-hover": !assigneeId,
                  })}
                  position="bottom-end"
                />
              )}
            </>
          ) : (
            <>
              {multiAssigneesEnabled ? (
                <MultiAssigneesPicker
                  projectId={projectId}
                  hiddenFromClients
                  assigneeIds={assigneeIds}
                  onChange={handleMultiAssigneesChange}
                  canEdit={true}
                  className={classNames({
                    "show-on-hover": !assigneeIds || assigneeIds?.length === 0,
                  })}
                />
              ) : (
                <ProjectMembersAssigneePicker
                  projectId={projectId}
                  assigneeId={assigneeId}
                  onChange={handleChangeAssignee}
                  className={classNames({
                    "show-on-hover": !assigneeId,
                  })}
                  position="bottom-end"
                />
              )}
            </>
          )}
          {shouldShowEstimate ? (
            <SelectTimeEstimate
              estimate={estimate}
              jobTypeId={jobTypeId}
              onChange={handleEstimate}
              setTabIndex={0}
              target={
                <div>
                  <Tooltip
                    title={
                      estimate ? `${formatHours(estimate)} of ${jobTypesMap[jobTypeId]}` : t(`Set Time Estimation`)
                    }
                  >
                    <IconButton
                      variant="text gray"
                      className={classNames({
                        "show-on-hover": !estimate,
                        "tw-px-2 tw-w-auto": estimate,
                      })}
                    >
                      {estimate ? (
                        <DisplayHours
                          variant="Caption 1"
                          value={estimate}
                        />
                      ) : (
                        <EstimatePlusIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                </div>
              }
            />
          ) : null}
        </div>
      ) : (
        <div className="tw-w-20" />
      )}
    </div>
  );
};

SuggestedItem.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  projectId: PropTypes.number.isRequired,
  type: PropTypes.string,
  onUpdate: PropTypes.func,
  assigneeId: PropTypes.number,
  assigneeIds: PropTypes.array,
  startOn: PropTypes.number,
  dueOn: PropTypes.number,
  autoFocus: PropTypes.bool,
  invalid: PropTypes.bool,
  inputName: PropTypes.string,
  selectionMode: PropTypes.string,
  disabled: PropTypes.bool,
  isPreview: PropTypes.bool,
  shouldShowEstimate: PropTypes.bool,
  allowAllUsers: PropTypes.bool,
  estimate: PropTypes.string,
  jobTypeId: PropTypes.number,
};

SuggestedItem.defaultProps = {
  shouldShowEstimate: false,
  allowAllUsers: false,
  estimate: "",
  invalid: false,
};
